import { useEffect, useRef, useState } from "react";
import {
  normalizeOptions,
  fetchCountries,
  fetchSectors,
  fetchIndustries,
  fetchStates,
  fetchCompanyNames,
  fetchCompanySizes,
  fetchCompanyRevenues,
  fetchHighestEducations,
  fetchWorkStatuses,
  fetchPrimaryProfessions,
  fetchTotalWorkExperience,
  fetchSeniorityLevels,
  fetchWorkExCurrentCompany,
  fetchBusinessModels,
  fetchCompanyTypes,
  fetchHouseholdIncomes,
  fetchEmploymentStatuses,
  fetchDepartments,
} from "../service/lookupService";

const EMPTY = [];

// No lookup endpoint exists for gender, so these fixed codes (as given) are
// used directly instead of an API call.
const GENDER_OPTIONS = [
  { id: 1, name: "Male" },
  { id: 2, name: "Female" },
  { id: 3, name: "Other" },
];

const STATIC_FETCHERS = {
  countries: fetchCountries,
  sectors: fetchSectors,
  companyNames: () => fetchCompanyNames(),
  companySizes: fetchCompanySizes,
  companyRevenues: fetchCompanyRevenues,
  highestEducations: fetchHighestEducations,
  workStatuses: fetchWorkStatuses,
  professions: fetchPrimaryProfessions,
  workExperiences: fetchTotalWorkExperience,
  seniorityLevels: fetchSeniorityLevels,
  tenureOptions: fetchWorkExCurrentCompany,
  businessModels: fetchBusinessModels,
  companyTypes: fetchCompanyTypes,
  householdIncomes: fetchHouseholdIncomes,
  employmentStatuses: fetchEmploymentStatuses,
  departments: fetchDepartments,
};

// Fetches every non-cascading dropdown's option list once (in parallel), and
// exposes loadStatesForCountry/loadIndustriesForSector for the two fields
// whose options depend on another field's selection. A failed call just
// leaves that one list empty rather than breaking the rest - the field falls
// back to whatever static list the caller passes in.
export function useLookupOptions() {
  const [options, setOptions] = useState({
    genders: GENDER_OPTIONS,
    countries: EMPTY,
    sectors: EMPTY,
    states: EMPTY,
    industries: EMPTY,
    companyNames: EMPTY,
    companySizes: EMPTY,
    companyRevenues: EMPTY,
    highestEducations: EMPTY,
    workStatuses: EMPTY,
    professions: EMPTY,
    workExperiences: EMPTY,
    seniorityLevels: EMPTY,
    tenureOptions: EMPTY,
    businessModels: EMPTY,
    companyTypes: EMPTY,
    householdIncomes: EMPTY,
    employmentStatuses: EMPTY,
    departments: EMPTY,
  });
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (!localStorage.getItem("token")) {
      setLoading(false);
      return () => {
        mounted.current = false;
      };
    }

    const keys = Object.keys(STATIC_FETCHERS);
    Promise.allSettled(keys.map((key) => STATIC_FETCHERS[key]())).then((results) => {
      if (!mounted.current) return;
      const patch = {};
      results.forEach((result, i) => {
        patch[keys[i]] = result.status === "fulfilled" ? normalizeOptions(result.value) : EMPTY;
      });
      setOptions((prev) => ({ ...prev, ...patch }));
      setLoading(false);
    });

    return () => {
      mounted.current = false;
    };
  }, []);

  const loadStatesForCountry = (countryId) => {
    if (!countryId) {
      setOptions((prev) => ({ ...prev, states: EMPTY }));
      return;
    }
    fetchStates([countryId])
      .then((res) => mounted.current && setOptions((prev) => ({ ...prev, states: normalizeOptions(res) })))
      .catch(() => mounted.current && setOptions((prev) => ({ ...prev, states: EMPTY })));
  };

  const loadIndustriesForSector = (sectorId) => {
    if (!sectorId) {
      setOptions((prev) => ({ ...prev, industries: EMPTY }));
      return;
    }
    fetchIndustries([sectorId])
      .then((res) => mounted.current && setOptions((prev) => ({ ...prev, industries: normalizeOptions(res) })))
      .catch(() => mounted.current && setOptions((prev) => ({ ...prev, industries: EMPTY })));
  };

  // Re-runs one non-cascading list's fetch on demand - e.g. when a field
  // that switched to "Other" text entry is backed out of, so the dropdown
  // it returns to shows a freshly-fetched list rather than whatever (possibly
  // empty, if the first load failed) it had before.
  const refetchList = (key) => {
    const fetcher = STATIC_FETCHERS[key];
    if (!fetcher) return;
    fetcher()
      .then((res) => mounted.current && setOptions((prev) => ({ ...prev, [key]: normalizeOptions(res) })))
      .catch(() => {});
  };

  return { options, loading, loadStatesForCountry, loadIndustriesForSector, refetchList };
}
